import type { ReadinessState } from '@erp/shared-utils';
import type { CartOwner } from '../components/supplierAssignmentsInput';
import type { BoardKind } from './buyerBoardColumns';

type Translate = (message: string, options?: Record<string, unknown>) => string;

/** The open order's owner: a vendor on the buyer board, a source on transfers. */
export function selectedOwnerFor(
  kind: BoardKind,
  params: { supplierId?: string; sourceId?: string },
): CartOwner | null {
  if (kind === 'po') {
    return params.supplierId
      ? { kind: 'po', supplierId: params.supplierId }
      : null;
  }
  return params.sourceId
    ? { kind: 'to', sourceWarehouseId: params.sourceId }
    : null;
}

const nonEmpty = <T>(values: T[]): T[] | undefined =>
  values.length > 0 ? values : undefined;

/**
 * The Orders list's owner filters: vendors and readiness on the buyer board,
 * source locations on the transfer board (GEN-3026).
 */
export function ownerListFilters(
  kind: BoardKind,
  picks: {
    vendorIds: string[];
    sourceIds: string[];
    readiness: ReadinessState[];
  },
): {
  supplierIds: string[] | undefined;
  sourceWarehouseIds: string[] | undefined;
  readiness: ReadinessState[] | undefined;
} {
  const po = kind === 'po';
  return {
    supplierIds: po ? nonEmpty(picks.vendorIds) : undefined,
    sourceWarehouseIds: po ? undefined : nonEmpty(picks.sourceIds),
    readiness: po ? nonEmpty(picks.readiness) : undefined,
  };
}

/** The owner filter's summary: vendors, or source locations. */
export function ownerFilterSummary(
  kind: BoardKind,
  gt: Translate,
  picks: { vendorIds: string[]; sourceIds: string[] },
): {
  vendorCount: number;
  allVendorsLabel: string;
  vendorsLabel: (count: number) => string;
} {
  if (kind === 'po') {
    return {
      vendorCount: picks.vendorIds.length,
      allVendorsLabel: gt('All vendors', {
        context: 'replenishment vendor filter summary, nothing narrowed',
      }),
      vendorsLabel: (count) =>
        gt('Vendors: {count}', {
          context: 'replenishment vendor filter summary',
          count: String(count),
        }),
    };
  }
  return {
    vendorCount: picks.sourceIds.length,
    allVendorsLabel: gt('All sources', {
      context:
        'transfer board source location filter summary, nothing narrowed',
    }),
    vendorsLabel: (count) =>
      gt('Sources: {count}', {
        context: 'transfer board source location filter summary',
        count: String(count),
      }),
  };
}

/** The order type tag in the open order's summary. */
export function orderKindTag(kind: BoardKind, gt: Translate): string {
  return kind === 'po'
    ? gt('PO', { context: 'purchase order type tag' })
    : gt('TO', { context: 'transfer order type tag' });
}
